import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import classnames from 'classnames';

import TextInput from 'generic/Forms/TextInput/TextInput.react';
import BlockyDatePicker from 'client/generic/BlockyDatePicker/BlockyDatePicker.react';
import TimePicker from 'client/generic/TimePicker/TimePicker.react';
import Button from 'client/styleguide/Button/Button.react';

import withTracking from 'lib/hocs/withTracking';

import './date-time-picker.scss';

/**
 * Given a datetime, this component will break that up into a date and a time of day,
 * and will create two inputs to make the datetime selection easy to use.
 *
 * Props:
 * - error: A string of any errors that should display before the inputs. The existance of this string
 *   means there is an error. I.e. do not pass an error string if no error exists.
 * - dateLabel: The label of the date input
 * - timeLabel: The label of the time input
 * - onChange: Callback to call when the datetime changes. Your callback will receive a moment object
 *   as its argument. If the inputs are being cleared, your callback will receive `null` as its argument
 * - placeholder: A placeholder that will be displayed within the date input if no value is currently selected
 * - required: Boolean denoting whether or not this field is required. If it is not required, a clear button
 *   will appear next to the inputs so the dates can be nullified.
 * - value: The current value of this input
 * - className: Class to give to the wrapper element
 *
 * @example
 * <DateTimePicker
 *   required
 *   dateLabel='Due Date'
 *   timeLabel='Due Time'
 *   value={assignment.getDueDate()}
 *   onChange={this.updateDueDate}
 *   error={dueDateError ? dueDateError.first() : null}
 * />
 */

const TrackedTimePicker = withTracking(TimePicker, {
  onFocus: {event: 'click', type: 'input', content: (e, props) => props.label}
});

const TrackedBlockyDatePicker = withTracking(BlockyDatePicker, {
  onFocus: {event: 'click', type: 'input', content: (e, props) => props.label}
});

const customDefaultTime = '00:00';

export default class DateTimePicker extends React.Component {
  static propTypes = {
    className: PropTypes.string,
    defaultValue: PropTypes.instanceOf(moment),
    dateError: PropTypes.string,
    dateLabel: PropTypes.string,
    customDefaultTime: PropTypes.string,
    timeError: PropTypes.string,
    timeLabel: PropTypes.string,
    onChange: PropTypes.func,
    placeholder: PropTypes.string,
    required: PropTypes.bool
  };

  static defaultProps = {
    customDefaultTime,
    dateLabel: 'Date',
    defaultValue: null,
    onChange: () => {},
    timeLabel: 'Time'
  };

  constructor(props) {
    super(props);

    const date = moment(props.defaultValue);

    this.initialState = {
      date: date.isValid() ? date.format('YYYY-MM-DD') : '',
      dateInputIsFocused: false,
      time: date.isValid() ? date.format('HH:mm') : props.customDefaultTime
    };

    this.state = this.initialState;
  }

  componentDidUpdate(_, prevState) {
    const {date, dateInputIsFocused, time} = this.state;
    // validation logic here - resetting etc.
    const focusHasMoved = dateInputIsFocused !== prevState.dateInputIsFocused;
    if (focusHasMoved && dateInputIsFocused) {
      this.dateInputRef.focus();
    }

    const dateHasChanged = prevState.date !== date;
    const timeHasChanged = prevState.time !== time;

    if (dateHasChanged || timeHasChanged) {
      this.props.onChange({date, time});
    }
  }

  makeDateInputRef = (node) => {
    this.dateInputRef = node;
  };

  clearValue = () => this.setState({date: '', time: this.props.customDefaultTime});

  render() {
    const {dateError, dateLabel, timeError, timeLabel, placeholder, className, required} =
      this.props;
    const {dateInputIsFocused, date} = this.state;
    const clearButton = !this.props.required ? (
      <Button
        className={classnames({
          'u-align-self_flex-end': !timeError && !dateError,
          'u-align-self_center': timeError || dateError
        })}
        linkButton
        text='Clear'
        onClick={this.clearValue}
      />
    ) : null;
    const showFields = !placeholder || dateInputIsFocused || date;

    return (
      <div className={classnames('date-time-picker', className)}>
        <div className='date-time-picker__inputs-wrapper'>
          <div className='date-time-picker__date-wrapper'>
            {!showFields ? (
              <TextInput
                required={required}
                className='date-time-picker__placeholder'
                label={dateLabel}
                onFocus={() => this.setState({dateInputIsFocused: true})}
                placeholder={placeholder}
              />
            ) : (
              <TrackedBlockyDatePicker
                className='date-time-picker__date'
                data-testid='assignment_date'
                error={!!dateError}
                errorMessage={dateError}
                inputRef={this.makeDateInputRef}
                label={dateLabel}
                name={dateLabel}
                onFocus={() => {}}
                onBlur={() => this.setState({dateInputIsFocused: false})}
                onChange={(e) => this.setState({date: e.target.value})}
                required={required}
                value={date}
              />
            )}
          </div>
          {!showFields ? (
            <TextInput className='date-time-picker__time' disabled label={timeLabel} />
          ) : (
            <TrackedTimePicker
              className='date-time-picker__time'
              disabled={Boolean(date) === false}
              error={!!timeError}
              errorMessage={timeError}
              label={timeLabel}
              onFocus={() => {}}
              onChange={(e) => this.setState({time: e.target.value || '00:00'})}
              onBlur={this.handleTimeInputBlur}
              placeholder='e.g. 10:30 AM'
              value={this.state.time}
            />
          )}
        </div>
        {clearButton}
      </div>
    );
  }
}
